import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
    FormHelperText,
    Box,
    Chip,
    Typography,
    IconButton,
    InputAdornment,
    Tooltip,
} from "@mui/material";
import { Add, Delete, Info } from "@mui/icons-material";
import { addDays, differenceInDays, formatDate, toLocalDay } from "../utils/dateUtils";
import styled from "@emotion/styled";
import MarkdownEditor from "./MarkdownEditor";
import UserAssignmentDialog from "./UserAssignmentDialog";
import { showAlert } from "../redux/slices/alerts";
import { useSelector, useDispatch } from "react-redux";
import { createProject } from "../redux/ducks/project";
import AppDatePicker from "./AppDatePicker";
import { getSkills } from "../redux/slices/skill";
import { getDepartments, getDepartmentByUser } from "../redux/ducks/department";
import { useNavigate } from "react-router-dom";
import { useHasPermission } from "../utils/permissionUtils";
import { useIsLocked } from "../utils/lockUtils";
import FilePondUploader from "./FilePondUploader";
import { restoreAttachments } from "../utils/uploadUtils";
import RetainerOverageNotice from "./RetainerOverageNotice";
import { useRetainerStatus } from "../hooks/useRetainerStatus";
import { selectableSkills, skillOptionLabel } from "../utils/skillUtils";
import TagPicker, { tagSourceNames, useTagDraft } from "./TagPicker";

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(1),
    },
}));

const FormField = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const AlignedFormField = styled(FormField)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0),
}));

const Row = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
}));

const HalfWidthField = styled(FormField)(({ theme }) => ({
    width: "50%",
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(4),
}));

const UserChipsContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
}));

const PaymentItem = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "flex-start",
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
}));

const PaymentFields = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(2),
    flex: 1,
}));

const AuthorizationHint = styled(FormHelperText)({
    margin: 0,
});

const EmptyPaymentsHint = styled(Typography)({
    fontStyle: 'italic',
});

const CreateProjectDialog = ({ open, onClose, accountId, accountData, accountGroups, groupId = null }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const skills = useSelector((state) => state.skills);
    const [departments, setDepartments] = useState([]);
    const groups = accountGroups;
    const today = new Date();
    const [readyToSubmit, setReadyToSubmit] = useState(false);
    const sevenDaysLater = addDays(today, 7);
    const hasPermission = useHasPermission();
    const userId = useSelector((state) => state.auth.userId);
    const canAssignUsers = hasPermission("assignProject");
    const canSeeInternal = hasPermission("canSeeInternal");
    const isLocked = useIsLocked(accountData, accountData?.client);
    const { status: retainerStatus } = useRetainerStatus({ accountId: accountData?._id, enabled: open });
    const [overageOverride, setOverageOverride] = useState(false);
    const [requiresAuthorization, setRequiresAuthorization] = useState(false);
    const isOverageBlocked = retainerStatus?.level === "block" && !overageOverride;
    const [attachments, setAttachments] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [group, setGroup] = useState(groupId);
    const [payments, setPayments] = useState([]);
    const tagParentType = group ? "Group" : "Account";
    const tagParentId = group || accountId;
    const tagClientId = accountData?.client?._id || accountData?.client || null;
    const tagDraft = useTagDraft({ enabled: open, parentType: tagParentType, parentId: tagParentId });
    const tagsRequired = canSeeInternal && !!accountData?.client?.requireProjectTags && !tagDraft.loading && !tagDraft.inheritedFailed && tagDraft.value.length === 0;
    const tagSources = tagSourceNames(accountData, accountData?.client, (groups || []).find((item) => item._id === group));
    const [projectData, setProjectData] = useState({
        name: "",
        overview: "",
        attachments: [],
        account: accountId,
        group: group,
        priority: "Medium",
        startDate: toLocalDay(today),
        dueDate: toLocalDay(sevenDaysLater),
        privacy: "Public",
        billingTerms: {
            type: "Parent",
            billingEntity: false,
        },
        skill: null,
        responsible: null,
        assignees: [],
        budget: {
            budgeted: 0,
            quoted: 0,
        },
        rush: false,
    });

    useEffect(() => {
        if (skills.length === 0) {
            dispatch(getSkills());
        }
    }, [skills, accountId, dispatch]);

    useEffect(() => {
        if (canSeeInternal) {
            dispatch(getDepartments(0, 100, (departments) => setDepartments(departments || [])));
            dispatch(getDepartmentByUser(userId, (department) => setProjectData((prevData) => ({ ...prevData, department: department?._id || null }))));
        }
    }, [dispatch, userId, canSeeInternal]);

    useEffect(() => {
        if (groupId) {
            setGroup(groupId);
            setProjectData((prevData) => ({ ...prevData, group: groupId }));
        }
    }, [groupId]);

    useEffect(() => {
        if (open) {
            setOverageOverride(false);
        } else {
            setRequiresAuthorization(false);
        }
    }, [open]);

    const [isUserAssignmentOpen, setIsUserAssignmentOpen] = useState(false);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        if (name === "budgeted" || name === "quoted") {
            setProjectData((prevData) => ({
                ...prevData,
                budget: {
                    ...prevData.budget,
                    [name]: parseFloat(value) || 0,
                },
            }));
        } else if (name === "group") {
            setGroup(value);
            setProjectData((prevData) => ({ ...prevData, group: value }));
        } else if (name === "billingTerms") {
            setProjectData((prevData) => ({ 
                ...prevData, 
                billingTerms: { 
                    ...prevData.billingTerms, 
                    type: value 
                } 
            }));
            // Clear payments if billing type is not "Fixed Price"
            if (value !== "Fixed Price") {
                setPayments([]);
            }
        } else {
            setProjectData((prevData) => ({ ...prevData, [name]: value }));
            if (projectData.name && projectData.name.trim().length >= 2) {
                setReadyToSubmit(true);
            } else {
                setReadyToSubmit(false);
            }
        }
    };

    const handleOverviewChange = (content) => {
        setProjectData((prevData) => ({ ...prevData, overview: content }));
    };

    const handleAddPayment = () => {
        const maxPaymentNumber = payments.length > 0 
            ? Math.max(...payments.map(p => p.paymentNumber || 0))
            : 0;
        setPayments([...payments, {
            paymentNumber: maxPaymentNumber + 1,
            value: "",
            date: "",
        }]);
    };

    const handleRemovePayment = (index) => {
        setPayments(payments.filter((_, i) => i !== index));
    };

    const handlePaymentChange = (index, field, value) => {
        const updated = [...payments];
        updated[index] = { ...updated[index], [field]: value };
        setPayments(updated);
    };

    const handleSubmit = () => {
        if (isLocked) return;
        if (isOverageBlocked) return;
        if (tagsRequired) return;
        setIsCreating(true);
        const apiData = {
            ...projectData,
            account: accountId,
            status: requiresAuthorization ? "Awaiting Authorization" : "Pending",
            attachments: restoreAttachments(attachments),
            ...tagDraft.assignments,
        };

        // Format payments if billing type is "Fixed Price" and payments exist
        if (projectData.billingTerms?.type === "Fixed Price" && payments.length > 0) {
            // Validate all payments have value > 0
            const invalidPayments = payments.filter(p => !p.value || parseFloat(p.value) <= 0);
            if (invalidPayments.length > 0) {
                setIsCreating(false);
                dispatch(showAlert({ severity: 'error', message: 'All payments must have a value greater than $0.00' }));
                return;
            }

            // Calculate total and percentages
            const total = payments.reduce((acc, p) => acc + (parseFloat(p.value) || 0), 0);
            const formattedPayments = payments.map(payment => ({
                paymentNumber: payment.paymentNumber,
                status: 'Pending',
                percentage: total > 0 ? ((parseFloat(payment.value) || 0) / total) * 100 : 0,
                value: parseFloat(payment.value) || 0,
                date: payment.date && payment.date !== '' ? new Date(payment.date) : null
            }));
            apiData.payments = formattedPayments;
            apiData.totalPayment = total;
        }
        
        dispatch(
            createProject(apiData, (response) => {
                if (response && response.id) {
                    setIsCreating(false);
                    navigate(`/account/${response.account.slug}/project/${response.id}`);
                } else {
                    setIsCreating(false);
                    if (response) dispatch(showAlert({ severity: 'error', message: 'Failed to create project' }));
                }
            })
        );
    };

    const handleOpenUserAssignment = () => {
        setIsUserAssignmentOpen(true);
    };

    const handleCloseUserAssignment = () => {
        setIsUserAssignmentOpen(false);
    };

    const handleAssign = (projectId, assignees, responsible) => {
        setProjectData((prevData) => ({
            ...prevData,
            responsible: responsible,
            assignees: assignees.filter((user) => user._id !== responsible?._id),
        }));
    };

    const isDueDateWithinTwoDays = () => {
        if (!projectData.dueDate) return false;
        return differenceInDays(projectData.dueDate, toLocalDay()) <= 2;
    };

    useEffect(() => {
        isDueDateWithinTwoDays() ? setProjectData(prevData => ({ ...prevData, rush: true })) : setProjectData(prevData => ({ ...prevData, rush: false }));
    }, [projectData.dueDate]);

    return (
        <StyledDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Create Project</DialogTitle>
            <StyledDialogContent>
                <RetainerOverageNotice status={retainerStatus} requireOverride override={overageOverride} onOverrideChange={setOverageOverride} />
                <FormField>
                    <TextField fullWidth label="Project Name" name="name" value={projectData.name} onChange={handleInputChange} required />
                </FormField>

                <FormField>
                    <MarkdownEditor initialContent="" onChange={handleOverviewChange} placeholder="Project overview..." />
                </FormField>

                <FormField>
                    <FilePondUploader setAttachments={setAttachments} onUploadStatusChange={setIsUploading} />
                </FormField>

                {groups.filter((group) => !group.completedDate && !group.isDeleted).length > 0 && (
                    <FormField>
                        <FormControl fullWidth>
                            <InputLabel>Project Group</InputLabel>
                            <Select name="group" value={projectData.group} onChange={handleInputChange}>
                                <MenuItem value={null}>None</MenuItem>
                                {groups
                                    .filter((group) => !group.completedDate && !group.isDeleted)
                                    .map((group) => (
                                        <MenuItem key={group._id} value={group._id}>
                                            {group.name}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </FormField>
                )}

                <FormField>
                    <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select name="priority" value={projectData.priority} onChange={handleInputChange} label="Priority">
                            <MenuItem value="Lowest">Lowest</MenuItem>
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                            <MenuItem value="Highest">Highest</MenuItem>
                        </Select>
                    </FormControl>
                </FormField>

                <FormField>
                    <TagPicker draft={tagDraft} parentType={tagParentType} parentId={tagParentId} clientId={tagClientId} clientName={accountData?.client?.name} accountId={accountId || accountData} sourceNames={tagSources} required={tagsRequired} helperText={tagsRequired ? "This client requires at least one tag on new projects." : undefined} />
                </FormField>

                {canSeeInternal ? (
                    <Row>
                        <HalfWidthField>
                            <AppDatePicker
                                label="Start Date"
                                name="startDate"
                                value={projectData.startDate}
                                onChange={(v, n) => handleInputChange({ target: { name: n, value: v } })}
                            />
                        </HalfWidthField>
                        <HalfWidthField>
                            <AppDatePicker
                                label="Due Date"
                                name="dueDate"
                                value={projectData.dueDate}
                                onChange={(v, n) => handleInputChange({ target: { name: n, value: v } })}
                            />
                        </HalfWidthField>
                    </Row>
                ) : (
                    <FormField>
                        <AppDatePicker
                            label="Requested Due Date"
                            name="dueDate"
                            value={projectData.dueDate}
                            onChange={(v, n) => handleInputChange({ target: { name: n, value: v } })}
                        />
                    </FormField>
                )}

                {canSeeInternal && (
                    <>
                        <Row>
                            <HalfWidthField>
                                <FormControl fullWidth>
                                    <InputLabel>Privacy</InputLabel>
                                    <Select name="privacy" value={projectData.privacy || 'Public'} onChange={handleInputChange} label="Privacy">
                                        <MenuItem value="Public">Public</MenuItem>
                                        <MenuItem value="Internal">Internal</MenuItem>
                                        <MenuItem value="Private">Private</MenuItem>
                                    </Select>
                                </FormControl>
                            </HalfWidthField>

                            <HalfWidthField>
                                <FormControl fullWidth>
                                    <InputLabel>Billing Type</InputLabel>
                                    <Select name="billingTerms" value={projectData.billingTerms?.type || "Parent"} onChange={handleInputChange} label="Billing Type">
                                        <MenuItem value="Parent">Parent</MenuItem>
                                        <MenuItem value="Retainer">Retainer</MenuItem>
                                        <MenuItem value="Hourly">Hourly</MenuItem>
                                        <MenuItem value="Fixed Price">Fixed Price</MenuItem>
                                        <MenuItem value="Internal">Internal</MenuItem>
                                        <MenuItem value="Non Billable">Non Billable</MenuItem>
                                    </Select>
                                </FormControl>
                            </HalfWidthField>
                        </Row>

                        {projectData.billingTerms?.type === "Fixed Price" && (
                            <FormField>
                                <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
                                    <Typography variant="subtitle1">Payments</Typography>
                                    <Button
                                        startIcon={<Add />}
                                        onClick={handleAddPayment}
                                        size="small"
                                        variant="outlined"
                                    >
                                        Add Payment
                                    </Button>
                                </Box>
                                {payments.length > 0 ? (
                                    payments.map((payment, index) => (
                                        <PaymentItem key={index}>
                                            <PaymentFields>
                                                <TextField
                                                    fullWidth
                                                    label="Payment Value"
                                                    type="number"
                                                    value={payment.value || ''}
                                                    onChange={(e) => handlePaymentChange(index, 'value', e.target.value)}
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                                                    }}
                                                    inputProps={{ step: 0.01, min: 0.01 }}
                                                    size="small"
                                                />
                                                <AppDatePicker
                                                    label="Due Date"
                                                    value={formatDate(payment.date)}
                                                    onChange={(v) => handlePaymentChange(index, 'date', v)}
                                                    size="small"
                                                />
                                            </PaymentFields>
                                            <IconButton
                                                onClick={() => handleRemovePayment(index)}
                                                color="error"
                                                size="small"
                                            >
                                                <Delete />
                                            </IconButton>
                                        </PaymentItem>
                                    ))
                                ) : (
                                    <EmptyPaymentsHint variant="body2" color="text.secondary">
                                        No payments added. Click "Add Payment" to set up payment schedule.
                                    </EmptyPaymentsHint>
                                )}
                            </FormField>
                        )}

                        <FormField> 
                            <FormControl fullWidth>
                                <InputLabel>Department</InputLabel>
                                <Select name="department" value={projectData.department} onChange={handleInputChange} label="Department">
                                    {departments.map((department) => (
                                        <MenuItem key={department._id} value={department._id}>
                                            {department.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormField>

                        <FormField>
                            <FormControl fullWidth>
                                <InputLabel>Skill</InputLabel>
                                <Select name="skill" value={projectData.skill || ''} onChange={handleInputChange} label="Skill">
                                    {selectableSkills(skills, projectData.skill).map((skill) => (
                                        <MenuItem key={skill._id} value={skill._id}>
                                            {skillOptionLabel(skill)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormField>
                    </>
                )}

                {canAssignUsers && (
                    <FormField>
                        <Typography variant="subtitle1">Responsible & Assignees</Typography>
                        <UserChipsContainer>
                            {projectData.responsible && (
                                <Chip label={`${projectData.responsible.fullName} (Responsible)`} onDelete={() => setProjectData((prev) => ({ ...prev, responsible: null }))} />
                            )}
                            {projectData.assignees.map((assignee) => (
                                <Chip
                                    key={assignee._id}
                                    label={assignee.fullName}
                                    onDelete={() =>
                                        setProjectData((prev) => ({
                                            ...prev,
                                            assignees: prev.assignees.filter((a) => a._id !== assignee._id),
                                        }))
                                    }
                                />
                            ))}
                        </UserChipsContainer>
                        <Button onClick={handleOpenUserAssignment} variant="outlined">
                            Assign Users
                        </Button>
                    </FormField>
                )}

                <Row>
                    {canSeeInternal ? (
                        <>
                            <HalfWidthField>
                                <TextField fullWidth label="Budgeted Time" name="budgeted" type="number" onChange={handleInputChange} />
                            </HalfWidthField>
                            <HalfWidthField>
                                <TextField fullWidth label="Quoted Time" name="quoted" type="number" onChange={handleInputChange} />
                            </HalfWidthField>
                        </>
                    ) : (
                        <FormControl fullWidth>
                            <TextField fullWidth label="Approved Budget in Hours" name="quoted" type="number" onChange={handleInputChange} />
                        </FormControl>
                    )}
                </Row>

                <AlignedFormField>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={projectData.rush}
                                disabled={isDueDateWithinTwoDays()}
                                name="rush"
                                onChange={(e) =>
                                    setProjectData((prev) => ({
                                        ...prev,
                                        rush: e.target.checked,
                                    }))
                                }
                            />
                        }
                        label="Rush Job"
                    />
                    <Tooltip title="Rush jobs are projects that are expected to be completed within the next 48 hours, this has a 100% surcharge applied to the project.">
                        <Info fontSize="small" color="primary" />
                    </Tooltip>
                </AlignedFormField>

                <FormField>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={requiresAuthorization}
                                name="requiresAuthorization"
                                onChange={(e) => setRequiresAuthorization(e.target.checked)}
                                inputProps={{ "aria-describedby": "requires-authorization-hint" }}
                            />
                        }
                        label="Requires Authorization"
                    />
                    <AuthorizationHint id="requires-authorization-hint">The project starts awaiting the client's authorization.</AuthorizationHint>
                </FormField>
            </StyledDialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!readyToSubmit || isUploading || isCreating || isLocked || isOverageBlocked || tagDraft.loading || tagsRequired}>
                    {isCreating ? (
                        <>
                            Creating Project... <CircularProgress size={20} />
                        </>
                    ) : (
                        "Create Project"
                    )}
                </Button>
            </DialogActions>

            <UserAssignmentDialog
                isOpen={isUserAssignmentOpen}
                onClose={handleCloseUserAssignment}
                objectId={accountId}
                objectType="project"
                onAssign={handleAssign}
                isNewObject={true}
                accountId={accountId}
            />
        </StyledDialog>
    );
};

export default CreateProjectDialog;
